<template>
    <div class="home-app-bar">
        <v-app-bar app elevation="1" height="80">
            <router-link to="/">
                <v-img
                    data-cy="getac-logo"
                    :src="logoUrl"
                    class="getac-nav-logo"
                    @click="
                        clearActive = true
                        isHome = true
                    "
                />
            </router-link>
            <v-spacer />

            <div>
                <v-tabs background-color="transparent" class="hidden-sm-and-down" optional show-arrows>
                    <template v-for="(navItem, i) in navigationItems">
                        <v-tab
                            v-if="!navItem.multiple"
                            :key="i"
                            :href="navItem.override"
                            :target="navItem.override && '_blank'"
                            :exact="false"
                            :ripple="false"
                            class="font-weight-regular nav-font"
                            :style="clearActive ? '' : activeItem(navItem.name)"
                            :class="{ initial: isHome }"
                            min-width="96"
                            :data-cy="navItem.dataCy"
                            hide-slider
                            text
                            @click="goTo(navItem)"
                        >
                            {{ navItem.title }}
                        </v-tab>
                        <v-menu
                            v-else-if="navItem.multiple && navItem.name === 'sm-inventory'"
                            :key="navItem.dataCy"
                            open-on-hover
                            bottom
                            offset-y
                            close-delay="250"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-tab
                                    data-cy="sm-inventory-tab"
                                    :exact="false"
                                    :ripple="false"
                                    class="font-weight-regular nav-font"
                                    min-width="96"
                                    hide-slider
                                    text
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="clearActive = true"
                                >
                                    {{ navItem.title }}
                                </v-tab>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in smInventoryDropdown"
                                    :key="index"
                                    align="start"
                                    @click="goToRoute(item.routeName)"
                                >
                                    <v-list-item-title class="nav-font">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-menu
                            v-else-if="navItem.multiple && navItem.name === 'create-rma'"
                            :key="navItem.name"
                            open-on-hover
                            bottom
                            offset-y
                            close-delay="250"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-tab
                                    data-cy="nav-create-rma"
                                    :exact="false"
                                    :ripple="false"
                                    class="font-weight-regular nav-font"
                                    min-width="96"
                                    hide-slider
                                    text
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="clearActive = true"
                                >
                                    {{ navItem.title }}
                                </v-tab>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in rmaUpgradeDropdown"
                                    :key="index"
                                    align="start"
                                    @click="goToRoute(item.routeName)"
                                >
                                    <v-list-item-title class="nav-font">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                    <v-menu v-if="$auth.isAuthenticated" open-on-hover bottom offset-y close-delay="250">
                        <template v-slot:activator="{ on, attrs }">
                            <v-tab
                                data-cy="signed-in-tab"
                                :to="{ name: 'your-account' }"
                                :exact="false"
                                :ripple="false"
                                class="font-weight-regular nav-font"
                                min-width="96"
                                hide-slider
                                text
                                v-bind="attrs"
                                v-on="on"
                                @click="clearActive = true"
                            >
                                Your Account
                            </v-tab>
                        </template>

                        <v-list>
                            <v-list-item v-if="hasGssOrgId" align="start" @click="yourDashboard">
                                <v-list-item-title class="nav-font">Dashboard</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                href="https://storage.googleapis.com/gusa-assets/documents/Getac%20Service%20Portal%20Manual.pdf"
                                target="_blank"
                            >
                                <v-list-item-title class="nav-font">Service Portal Manual</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-for="(item, index) in myAccountDropDown"
                                :key="index"
                                align="start"
                                @click="item.method"
                            >
                                <v-list-item-title class="nav-font">{{ item.title }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-tab
                        v-else
                        data-cy="sign-in-tab"
                        :ripple="false"
                        class="font-weight-regular nav-font"
                        min-width="96"
                        hide-slider
                        text
                        @click="signIn"
                    >
                        Sign In
                    </v-tab>
                </v-tabs>
            </div>

            <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer" />
        </v-app-bar>

        <home-drawer v-model="drawer" :items="drawerItems" :go-to="goTo" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import gql from 'graphql-tag'
// import { rolesMap } from '@/config'

export default {
    name: `HomeAppBar`,

    components: {
        HomeDrawer: () => import(`./drawer`)
    },
    apollo: {
        queryOrg: {
            query: gql`
                query QueryOrg {
                    queryOrg {
                        gssOrgId
                        orgId
                        orgName
                    }
                }
            `,
            update(data) {
                if (data && data.queryOrg && data.queryOrg.gssOrgId) {
                    this.hasGssOrgId = true
                }
            }
        },
        querySMCustomer: {
            query: gql`
                query QuerySMCustomer($customerId: String!) {
                    querySMCustomer(customerId: $customerId) {
                        smConfirmed
                    }
                }
            `,
            skip() {
                return !this.$auth.user.org || !this.$auth.user.org.branchId
            },
            variables() {
                return {
                    customerId: this.$auth.user.org.branchId
                }
            }
        }
    },

    data() {
        return {
            drawer: null,
            clearActive: false,
            path: this.$route.name,
            isHome: false,
            hasGssOrgId: false,
            logoUrl: process.env.VUE_APP_PUBLIC_ASSETS_URL + `/logos/getac-logo.svg`,
            items: [
                {
                    name: `create-rma`,
                    title: `Create RMA`,
                    dataCy: `nav-create-rma`
                },
                {
                    name: `parts-order`,
                    title: `Computer Parts Order`,
                    dataCy: `nav-parts-order`
                },
                {
                    name: `parts-usage`,
                    title: `Parts Usage`,
                    dataCy: `nav-parts-usage`
                },
                {
                    name: `upgrade-device`,
                    title: `Upgrade Device`,
                    dataCy: `nav-parts-usage`
                },
                {
                    name: `help-support`,
                    title: `Support`,
                    dataCy: `nav-help-support`
                }
            ]
        }
    },

    computed: {
        ...mapGetters({
            cart: `cart/cart`
        }),
        isMultiplRMAUser() {
            const userPerms = this.$auth.user && this.$auth.user.roles ? this.$auth.user.roles : []
            const userPermsArray = Array.isArray(userPerms) ? userPerms : [userPerms]
            return userPermsArray.includes('service:rma:multiple')
        },
        //        videoSerialPermission() {
        //            return this.$auth.allRoles(rolesMap.videoSerialPermission)
        //        },
        myAccountDropDown() {
            const dropdowns = [
                { title: `Your RMAs`, method: this.yourRmas },
                { title: `Your Parts Orders`, method: this.yourPartsOrders },
                {
                    title: `Product Registration`,
                    method: this.productRegistration
                },
                {
                    title: 'Saved Quotes',
                    method: this.yourQuotes
                }
            ]
            if (this.$auth.userInOrganization()) {
                dropdowns.push({ title: `Organizations`, method: this.organizations })
            }
            dropdowns.push(
                { title: `Account Settings`, method: this.yourAccount },
                { title: `Sign Out`, method: this.signOut }
            )
            return dropdowns
        },
        smInventoryDropdown() {
            const dropdowns = [
                { title: `Product Registration`, routeName: 'product-registration' },
                { title: `Inventory`, routeName: 'sm-inventory' }
                // {
                //     title: `Parts Usage`,
                //     routeName: 'parts-usage'
                // }
            ]
            if (this.querySMCustomer && !this.querySMCustomer.smConfirmed) {
                dropdowns.splice(2, 0, {
                    title: `Inventory Profile`,
                    routeName: 'sm-inventory-profile'
                })
            }
            return dropdowns
        },
        rmaUpgradeDropdown() {
            const dropdowns = [
                { title: `Create RMA`, routeName: 'create-rma' },
                { title: `Upgrade Device`, routeName: 'upgrade-device' }
            ]
            if (this.isMultiplRMAUser) {
                // insert multiple RMA option after Create RMA
                dropdowns.splice(1, 0, {
                    title: `Create Multiple Video RMA`,
                    routeName: 'create-rma-multi-sn'
                })
            }
            return dropdowns
        },
        navigationItems() {
            const navItems = [
                {
                    name: `create-rma`,
                    title: `Create RMA/Upgrade`,
                    dataCy: `nav-create-rma`,
                    multiple: true
                },
                {
                    name: `video-parts-order`,
                    title: `Video Parts Order`,
                    dataCy: `nav-video-parts-order`,
                    multiple: false
                },
                {
                    name: `parts-order`,
                    title: `Computer Parts Order`,
                    dataCy: `nav-parts-order`,
                    multiple: false
                },
                {
                    name: `sm-inventory`,
                    title: `Inventory`,
                    dataCy: `nav-sm-inventory`,
                    multiple: true
                },
                {
                    name: 'drivers',
                    title: `Drivers`,
                    dataCy: `nav-drivers`
                },
                {
                    name: `help-support`,
                    title: `Support`,
                    dataCy: `nav-help-support`,
                    multiple: false
                }
            ]
            return navItems
        },
        drawerItems() {
            if (this.$auth.isAuthenticated)
                return [
                    ...this.navigationItems,
                    { name: `your-account`, title: `Your Account` },
                    { name: `sign-out`, title: `Sign Out` }
                ]
            else return [...this.navigationItems, { name: `sign-in`, title: `Sign In` }]
        }
    },

    created() {
        this.checkCart()
    },

    methods: {
        ...mapActions({
            remove: `cart/remove`
        }),
        checkCart() {
            if (this.cart.length > 0) {
                function addDays(current, days) {
                    const date = new Date(current)
                    date.setDate(date.getDate() + days)
                    return date
                }
                this.cart.forEach((e) => {
                    const date = new Date()
                    const expired = addDays(e.storeDate, 2)
                    if (date > expired) {
                        this.remove({
                            ...e
                        })
                    }
                })
            }
        },
        activeItem(v) {
            const path = this.$route.name
            const current = v
            if (path === current) {
                return 'color: #db5120'
            } else {
                return ''
            }
        },
        goTo(v) {
            this.clearActive = true
            this.isHome = false
            if (v.name === `create-rma` && this.$route.name === `create-rma`) {
                v.query = {
                    refresh: Date.now()
                }
                const answer = window.confirm(`Do you really want to leave? You have unsaved changes..`)
                if (!answer) {
                    return
                }
            }

            if (v.name === 'sign-in') return this.signIn()

            this.$router.push(v)
        },
        signIn() {
            this.$auth.loginWithRedirect()
        },

        signOut() {
            this.$auth.logout()
        },

        yourAccount() {
            this.$router.push({ name: `account-settings` })
        },

        yourDashboard() {
            this.$router.push({ name: `dashboard` })
        },

        productRegistration() {
            this.$router.push({ name: `product-registration` })
        },

        yourRmas() {
            this.$router.push({ name: `rma-history` })
        },

        organizations() {
            this.$router.push({ name: `organizations` })
        },

        yourPartsOrders() {
            this.$router.push({ name: `parts-orders-history` })
        },

        // drivers() {
        //     this.$router.push({ name: `drivers` })
        // },

        yourQuotes() {
            this.$router.push({ name: `quotes` })
        },

        goToRoute(name) {
            this.$router.push({ name: name })
        }

        // savedQuotes() {
        //     this.$router.push({ name: 'your-account/saved-quotes' })
        // }
    }
}
</script>

<style lang="scss">
.home-app-bar {
    .v-tabs-slider {
        margin: 0 auto;
        max-width: 0;
    }

    .getac-nav-logo {
        max-width: 128px;
        width: 100%;
    }
}
.initial {
    color: rgba(0, 0, 0, 0.54) !important;
}
</style>
